import { VehicleCreate } from "../../api";
import { makeAddNewCallback } from "../../redux/slices/addNew";
import { configProps, Driver } from "../../types";
import DeliveryCard from "./components/DeliveryCard.tsx";
import { ColumnBodyOptions } from "primereact/column";

const chipList = <TField extends "trucks" | "trailers">(
  rowData: Driver,
  options: ColumnBodyOptions,
) => {
  const field = options.field as TField;
  const value = Array.isArray(rowData[field]) ? rowData[field] : [];

  return (
    <div className="custom-chip-list">
      {value.map((chip, index) => (
        <span key={index} className="custom-chip">
          {chip.toUpperCase()}
        </span>
      ))}
    </div>
  );
};

export const driversConfig: configProps<"drivers"> = {
  view: "drivers",
  title: "Kierowca",
  columns: [
    {
      id: 1,
      field: "firstName",
      header: "Imię",
      sortable: true,
      isDefaultVisible: true,
      className: "column--small",
      inputType: "text",
      required: true,
    },
    {
      id: 2,
      field: "lastName",
      header: "Nazwisko",
      sortable: true,
      isDefaultVisible: true,
      className: "column--small",
      inputType: "text",
      required: true,
    },
    {
      id: 3,
      field: "personalCode",
      header: "ID",
      sortable: true,
      isDefaultVisible: false,
      className: "column--small",
      inputType: "text",
      required: false,
    },
    {
      id: 4,
      field: "phone",
      header: "Telefon",
      sortable: true,
      isDefaultVisible: false,
      className: "column--small",
      inputType: "text",
      required: false,
    },
    {
      id: 5,
      field: "email",
      header: "E-mail",
      sortable: true,
      isDefaultVisible: false,
      className: "column--small",
      inputType: "text",
      required: false,
    },
    {
      id: 6,
      field: "companyId",
      header: "Firma",
      sortable: true,
      isDefaultVisible: true,
      className: "column--medium",
      inputType: "dropdown",
      optionsKey: "transporters",
      required: false,
      body: (rowData: Driver) => rowData.company?.name || "",
    },
    {
      id: 7,
      field: "managerId",
      header: "Kierownik",
      sortable: true,
      isDefaultVisible: true,
      className: "column--medium",
      inputType: "dropdown",
      optionsKey: "organizationalUsers",
      enableFilter: true,
      required: false,
      body: (rowData: Driver) => rowData.manager?.email || "",
    },
    {
      id: 8,
      field: "trucks",
      header: "Ciezarowki",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "chips",
      body: chipList<"trucks">,
      contextInputType: {
        driverTable: "chips",
        driverDialog: "dropdown",
        deliveryDialog: "dropdown",
        deliveryTable: "dropdown",
      },
      optionsKey: "trucks",
      allowAddNew: makeAddNewCallback<Driver>({
        field: "trucksIds",
        type: "vehicle",
        makeInitialValues: ({ initialValueString: number, row }) =>
          ({
            number,
            type: "truck",
            ...(row.driverId ? { driver_ids: [row.driverId] } : {}),
          }) satisfies Partial<VehicleCreate>,
      }),
    },
    {
      id: 9,
      field: "trailers",
      header: "Przyczepy",
      isDefaultVisible: true,
      className: "column--small",
      inputType: "chips",
      body: chipList<"trailers">,

      contextInputType: {
        driverTable: "chips",
        driverDialog: "dropdown",
        deliveryDialog: "dropdown",
        deliveryTable: "dropdown",
      },
      optionsKey: "trailers",
      allowAddNew: makeAddNewCallback<Driver>({
        field: "trailersIds",
        type: "vehicle",
        makeInitialValues: ({
          initialValueString: number,
          row,
        }: {
          initialValueString: string;
          row: { driverId: number | null };
        }) =>
          ({
            number,
            type: "trailer",
            ...(row.driverId ? { driver_ids: [row.driverId] } : {}),
          }) satisfies Partial<VehicleCreate>,
      }),
    },
    {
      id: 10,
      field: "scheduledDeliveries",
      header: "Dostawy zaplanowane",
      isDefaultVisible: true,
      className: "column--large",
      isDefaultSort: false,
      editable: false,
      body: (rowData: Driver) => {
        return (
          <div className="scheduled-list">
            {rowData.deliveries.map((delivery) => (
              <DeliveryCard
                key={`delivery-${delivery.id}-${delivery.type}`}
                delivery={delivery}
              />
            ))}
          </div>
        );
      },
    },
  ],
};
